import React, { memo } from 'react'
import { Box } from '@mui/material'
import { NoSymbolIcon } from '@heroicons/react/24/outline'
import { INTERAX_COLORS } from '../../../../constants/colors'
import { isMediaToggleButton, type WherebyToolbarButtonConfig } from './toolbarConfig'
import { WherebyToolbarButtonId } from '../../../../../../pure-js/enums/Whereby'

// ── Constants ─────────────────────────────────────────────────────────────────

export const MUTED_COLOR = '#F04438'
const MUTED_BG = 'rgba(240, 68, 56, 0.2)'
const MUTED_BG_HOVER = 'rgba(240, 68, 56, 0.3)'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ToolbarButtonViewModel {
  id: WherebyToolbarButtonId
  label: string
  onClick: () => void
  isActive?: boolean
  variant?: 'default' | 'destructive'
  Icon: WherebyToolbarButtonConfig['icon']
  MutedIcon?: WherebyToolbarButtonConfig['icon']
}

export interface ToolbarButtonProps {
  id: WherebyToolbarButtonId
  Icon: WherebyToolbarButtonConfig['icon']
  MutedIcon?: WherebyToolbarButtonConfig['icon']
  label: string
  onClick: () => void
  onSettingsOpen: (el: HTMLElement) => void
  isActive?: boolean
  variant?: 'default' | 'destructive'
  compact: boolean
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function getButtonBg(
  variant: string | undefined,
  isMuted: boolean,
  isMediaToggle: boolean,
  isActive: boolean | undefined,
  defaultBg: string
): string {
  if (variant === 'destructive') return '#F04438'
  if (isMuted) return MUTED_BG
  if (isMediaToggle && isActive) return INTERAX_COLORS.sun
  if (isActive) return `${INTERAX_COLORS.sun}33`
  return defaultBg
}

function getButtonHoverBg(
  variant: string | undefined,
  isMuted: boolean,
  isMediaToggle: boolean,
  isActive: boolean | undefined,
  defaultHoverBg: string
): string {
  if (variant === 'destructive') return '#D92D20'
  if (isMuted) return MUTED_BG_HOVER
  if (isMediaToggle && isActive) return INTERAX_COLORS.sun40
  if (isActive) return `${INTERAX_COLORS.sun}4D`
  return defaultHoverBg
}

// ── Component ─────────────────────────────────────────────────────────────────

export const ToolbarButton = memo(function ToolbarButton({
  id,
  Icon,
  MutedIcon,
  label,
  onClick,
  onSettingsOpen,
  isActive,
  variant,
  compact
}: ToolbarButtonProps) {
  const isMediaToggle = isMediaToggleButton(id)
  const isMuted = isMediaToggle && isActive === false
  const DisplayIcon = isMuted && MutedIcon ? MutedIcon : Icon
  const iconSize = compact ? 15 : 20
  const defaultBg = compact ? 'rgba(255,255,255,0.15)' : 'rgba(24, 26, 28, 0.8)'
  const defaultHoverBg = compact ? 'rgba(255,255,255,0.25)' : 'rgba(35, 38, 41, 0.9)'

  return (
    <Box
      component="button"
      type="button"
      onClick={(e: React.MouseEvent<HTMLElement>) => {
        if (id === WherebyToolbarButtonId.SETTINGS) {
          onSettingsOpen(e.currentTarget)
        } else {
          onClick()
        }
      }}
      aria-label={label}
      title={!compact ? label : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...(compact ? { width: 32, height: 32, flexShrink: 0 } : { padding: '10px' }),
        borderRadius: '999px',
        border: 'none',
        cursor: 'pointer',
        color: isMuted ? MUTED_COLOR : INTERAX_COLORS.white,
        backgroundColor: getButtonBg(variant, isMuted, isMediaToggle, isActive, defaultBg),
        transition: `background-color ${compact ? '0.15s' : '0.2s'} ease`,
        '&:hover': {
          backgroundColor: getButtonHoverBg(variant, isMuted, isMediaToggle, isActive, defaultHoverBg)
        }
      }}
    >
      {id === WherebyToolbarButtonId.MICROPHONE && isMuted ? (
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <DisplayIcon style={{ width: iconSize, height: iconSize }} />
          <NoSymbolIcon
            style={{
              width: compact ? 10 : 12,
              height: compact ? 10 : 12,
              position: 'absolute',
              bottom: compact ? -3 : -4,
              right: compact ? -3 : -4,
              color: MUTED_COLOR
            }}
          />
        </Box>
      ) : (
        <DisplayIcon style={{ width: iconSize, height: iconSize }} />
      )}
    </Box>
  )
})
